import { Star, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import Reveal from "./Reveal";
import SectionLabel from "./SectionLabel";
import { useSite } from "../context/SiteContext";

// Live Google Reviews would be populated from a backend integration with
// Google Places API (Place ID + API key) or a third-party widget. Until that
// is wired up, we intentionally show NO fake reviews — only an honest empty
// state with a button to view real reviews on Google.
export default function GoogleReviews({ reviews = [] }) {
  const { brand } = useSite();
  const hasReviews = reviews && reviews.length > 0;

  return (
    <section id="reviews" data-testid="google-reviews" className="py-24 md:py-32 bg-muted/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <SectionLabel>Google Reviews</SectionLabel>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <h2 className="font-display text-4xl md:text-5xl font-light max-w-3xl text-balance">
              What Our <em className="not-italic font-medium">Guests</em> Say
            </h2>
            <p className="text-muted-foreground max-w-md">
              Real, verified Google Business reviews — straight from our travellers, never edited.
            </p>
          </div>
        </Reveal>

        {hasReviews ? (
          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <article className="rounded-3xl bg-card border border-border p-7 h-full flex flex-col" data-testid={`review-${i}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <img src={r.profile_photo_url} alt={r.author_name} className="h-11 w-11 rounded-full object-cover" loading="lazy" />
                    <div>
                      <div className="font-medium leading-tight">{r.author_name}</div>
                      <div className="text-xs text-muted-foreground">{r.relative_time_description || r.date}</div>
                    </div>
                  </div>
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: r.rating }).map((_, k) => (
                      <Star key={k} className="h-3.5 w-3.5 fill-primary text-primary" />
                    ))}
                  </div>
                  <p className="text-foreground/85 leading-relaxed text-sm flex-1">{r.text}</p>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" aria-hidden><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.12a6.6 6.6 0 0 1 0-4.24V7.04H2.18a11 11 0 0 0 0 9.92l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.07.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
                    Posted via Google
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        ) : (
          <Reveal>
            <div className="rounded-3xl border border-dashed border-border bg-card p-10 md:p-14 text-center max-w-3xl mx-auto">
              <div className="mx-auto h-14 w-14 grid place-items-center rounded-full bg-primary/10 mb-5">
                <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.12a6.6 6.6 0 0 1 0-4.24V7.04H2.18a11 11 0 0 0 0 9.92l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.07.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-light mb-3">
                Our reviews live on Google
              </h3>
              <p className="text-muted-foreground max-w-xl mx-auto mb-7">
                We don&apos;t publish self-curated testimonials. To read every word our travellers have written about {brand.name}, head straight to our Google Business Profile.
              </p>
              <a href={brand.googleReviewsUrl} target="_blank" rel="noreferrer" data-testid="view-google-reviews">
                <Button className="rounded-full h-12 px-6">
                  View All Reviews on Google <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
